/**
 * Firebase-based authentication hook
 * Replaces the old API-based auth system
 */

import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode, useRef } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { 
  loginWithUsernameOrEmail,
  loginWithGoogle,
  registerWithEmail,
  logout,
  getCurrentUserWithProfile,
  onAuthStateChangeWithProfile,
  setUserUsername,
  hasUsername,
  AuthResult
} from '../lib/auth-complete';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../lib/firebase';
import { noteShownAccount } from '../lib/stale-account';
import { setSentryUserContext } from '../lib/sentry';
import { runAsyncSafely } from '../lib/error-monitoring';
import { captureErrorToSentry } from '../lib/sentry';
import { normalizeRoles } from '../lib/normalize-user-data';

interface AuthUser extends FirebaseUser {
  /** Alias for uid — the Firestore document ID is the user's UID. */
  id: string;
  profile?: any;
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  login: (usernameOrEmail: string, password: string) => Promise<AuthResult>;
  loginWithGoogle: () => Promise<AuthResult>;
  register: (email: string, password: string, name: string) => Promise<AuthResult>;
  logout: () => Promise<void>;
  setUsername: (username: string) => Promise<AuthResult>;
  hasUsername: () => Promise<boolean>;
  isAuthenticated: boolean;
  refreshUser: () => Promise<void>;
  syncSubscription: () => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
let hasReportedMissingAuthProvider = false;

const missingProviderResult: AuthResult = {
  success: false,
  error: 'Authentication context unavailable',
  errorCode: 'AUTH_CONTEXT_UNAVAILABLE',
};

const fallbackAuthContext: AuthContextType = {
  user: null,
  isLoading: false,
  login: async () => missingProviderResult,
  loginWithGoogle: async () => missingProviderResult,
  register: async () => missingProviderResult,
  logout: async () => {},
  setUsername: async () => missingProviderResult,
  hasUsername: async () => false,
  isAuthenticated: false,
  refreshUser: async () => {},
  syncSubscription: async () => null,
};

export function FirebaseAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const userRef = useRef<AuthUser | null>(null);

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChangeWithProfile(
      (authUser) => {
        setUser(authUser);
        userRef.current = authUser; // Keep ref in sync
        noteShownAccount(authUser?.id ?? null, () => auth.currentUser?.uid ?? null);
        setIsLoading(false);
      },
      ({ hadAccount, hasAccount }) => {
        // Let go of the old account at once (its listeners close), and wait
        // on isLoading for the new one rather than look signed out.
        if (hadAccount) {
          setUser(null);
          userRef.current = null;
          noteShownAccount(null);
        }
        if (hasAccount) setIsLoading(true);
      }
    );

    // Listen for auth refresh events
    const handleAuthRefresh = async () => {
      console.log('Auth refresh requested - reloading user profile');
      const updatedUser = await getCurrentUserWithProfile();
      if (updatedUser) {
        console.log('Updated user profile loaded:', updatedUser.profile);
        setUser(updatedUser);
        userRef.current = updatedUser;
        noteShownAccount(updatedUser.id ?? null);
      }
    };

    document.addEventListener('auth-refresh-required', handleAuthRefresh);

    return () => {
      unsubscribe();
      document.removeEventListener('auth-refresh-required', handleAuthRefresh);
    };
  }, []); // Remove user dependency to prevent infinite loop

  useEffect(() => {
    const normalizedRoles = user ? normalizeRoles(user) : [];

    // Tier/status: `SentryContextSync` + `syncSentrySubscriptionFromApp` (subscriptions/{uid}).
    setSentryUserContext(user ? {
      id: user.uid,
      email: user.email,
      username: user.displayName || user.profile?.username || null,
      subscriptionTier: null,
      roles: normalizedRoles,
    } : null);
  }, [user]);

  const handleLogin = async (usernameOrEmail: string, password: string): Promise<AuthResult> => {
    const result = await loginWithUsernameOrEmail(usernameOrEmail, password);
    return result;
  };

  const handleGoogleLogin = async (): Promise<AuthResult> => {
    const result = await loginWithGoogle();
    return result;
  };

  const handleRegister = async (email: string, password: string, name: string): Promise<AuthResult> => {
    const result = await registerWithEmail(email, password, name);
    return result;
  };

  const handleLogout = async (): Promise<void> => {
    await logout();
  };

  const handleSetUsername = async (username: string): Promise<AuthResult> => {
    const result = await setUserUsername(username);
    
    if (result.success) {
      // Refresh user data
      const updatedUser = await getCurrentUserWithProfile();
      setUser(updatedUser);
      userRef.current = updatedUser;
    }
    
    return result;
  };

  const refreshUser = async (): Promise<void> => {
    const updatedUser = await getCurrentUserWithProfile();
    if (updatedUser) {
      setUser(updatedUser);
      userRef.current = updatedUser;
    }
  };

  const syncSubscription = async (): Promise<any> => {
    return runAsyncSafely(async () => {
      console.log('🔄 MANUAL SUBSCRIPTION SYNC: Starting manual sync via useFirebaseAuth hook');
      
      const syncFn = httpsCallable(functions, 'syncUserSubscription');
      const result = await syncFn();
      
      console.log('✅ MANUAL SUBSCRIPTION SYNC: Function completed, refreshing user profile');
      
      // Refresh user after sync to get updated subscription data
      await refreshUser();
      
      console.log('✅ MANUAL SUBSCRIPTION SYNC: User profile refreshed');
      
      return result.data;
    }, 'manualSubscriptionSync');
  };

  // Memoize the context value so consumers don't re-render on every provider render.
  // Without this, any internal state change here produces a fresh value object, which
  // cascades into remounts of inline-declared route components (flicker on wizard/forms).
  const value: AuthContextType = useMemo(
    () => ({
      user,
      isLoading,
      login: handleLogin,
      loginWithGoogle: handleGoogleLogin,
      register: handleRegister,
      logout: handleLogout,
      setUsername: handleSetUsername,
      hasUsername,
      isAuthenticated: !!user,
      refreshUser,
      syncSubscription,
    }),
    [user, isLoading],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useFirebaseAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    const error = new Error('useFirebaseAuth must be used within a FirebaseAuthProvider');

    if (import.meta.env.DEV) {
      throw error;
    }

    if (!hasReportedMissingAuthProvider) {
      hasReportedMissingAuthProvider = true;
      captureErrorToSentry(error, {
        context: 'authContextMissing',
        tags: {
          source: 'useFirebaseAuth',
          severity: 'warning',
        },
      });
      console.warn('Auth context missing; falling back to signed-out state.');
    }

    return fallbackAuthContext;
  }
  return context;
}

// Helper hooks for common auth checks
export function useRequireAuth() {
  const { user, isLoading } = useFirebaseAuth();
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    if (!isLoading && !user) {
      setShouldRedirect(true);
    }
  }, [user, isLoading]);

  return { user, isLoading, shouldRedirect };
}

export function useIsAdmin() {
  const { user } = useFirebaseAuth();
  return normalizeRoles(user).includes('ADMIN');
}

export function useIsInstructor() {
  const { user } = useFirebaseAuth();
  const roles = normalizeRoles(user);
  return roles.includes('INSTRUCTOR') || roles.includes('ADMIN');
}
